import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import DrawerViewUserGroup from '@app/src/components/Drawer/Views/DrawerViewUserGroup'
import FinalRiskRatingCell from '@app/src/components/Table/Cells/FinalRiskRatingCell'
import UserGroup from '@app/src/components/Ui/UserGroup'
import { ProviderPriority, SupplierUsage } from '@app/src/types/categories'
import type { Provider, Supplier } from '@app/src/types/organizations'
import { ResourceTypes } from '@app/src/wf-constants'
import { Box, Chip, Stack, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'

interface CategoriesSectionProps {
  provider: Provider
}

const CategoryRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between">
    <Typography color="text.secondary" sx={{ flexShrink: 0, mr: 2 }}>
      {label}
    </Typography>
    <Box>{children}</Box>
  </Stack>
)

const CategoryChip: React.FC<{ label: string; hasValue: boolean }> = ({ label, hasValue }) => (
  <Chip variant="outlined" size="small" disabled={!hasValue} label={label} />
)

const isSupplierType = (provider: Provider): provider is Supplier =>
  provider?.type.toLowerCase() === ResourceTypes.Supplier

const CategoriesSection: React.FC<CategoriesSectionProps> = ({ provider }) => {
  const { formatMessage } = useIntl()
  const { openDrawer } = useDrawer()

  const hasOwners = Boolean(provider.ownerUsers?.length)
  const hasActivityStatus = Boolean(provider.activityStatus && provider.activityStatus !== 'NotSelected')
  const hasFinalRiskRating = Boolean(provider.finalRiskRating)
  const hasApprovalStatus = Boolean(
    provider.providerApprovalStatus && provider.providerApprovalStatus !== 'NotSelected',
  )
  const hasSupplierUsage = Boolean(provider.supplierUsage && provider.supplierUsage !== 'NotSelected')
  const hasTier = Boolean(provider.tier)
  const hasPriority = Boolean(provider.priority && provider.priority !== 'NotSelected')

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} mb={1}>
        {formatMessage({ id: 'resourceTypes.categories' })}
      </Typography>

      <Stack spacing={3} sx={{ bgcolor: 'common.white', borderRadius: 2, px: 3, py: 4 }}>
        <CategoryRow label={formatMessage({ id: 'schemas.provider.ownerUsers' })}>
          {hasOwners ? (
            <UserGroup
              users={provider.ownerUsers}
              label={formatMessage({ id: 'general.owners' }, { count: provider.ownerUsers?.length })}
              onClick={() =>
                openDrawer(
                  <DrawerViewUserGroup
                    title={formatMessage({ id: 'schemas.provider.ownerUsers' })}
                    subTitle={provider.name}
                    users={provider.ownerUsers}
                  />,
                )
              }
            />
          ) : (
            <CategoryChip label={formatMessage({ id: 'general.notAdded' })} hasValue={false} />
          )}
        </CategoryRow>

        <CategoryRow label={formatMessage({ id: 'schemas.provider.activityStatus' })}>
          <CategoryChip
            label={formatMessage({
              id: `schemas.provider.activityStatusValues.${provider.activityStatus ?? 'NotSelected'}`,
            })}
            hasValue={hasActivityStatus}
          />
        </CategoryRow>

        <CategoryRow label={formatMessage({ id: 'schemas.provider.finalRiskRating' })}>
          {hasFinalRiskRating ? (
            <FinalRiskRatingCell color="textSecondary" value={provider.finalRiskRating} disableCell />
          ) : (
            <CategoryChip label={formatMessage({ id: 'general.notSelected' })} hasValue={false} />
          )}
        </CategoryRow>

        <CategoryRow label={formatMessage({ id: 'schemas.provider.providerApprovalStatus' })}>
          <CategoryChip
            label={formatMessage({
              id: `schemas.provider.providerApprovalStatusValues.${provider.providerApprovalStatus ?? 'NotSelected'}`,
            })}
            hasValue={hasApprovalStatus}
          />
        </CategoryRow>

        {isSupplierType(provider) && (
          <>
            <CategoryRow label={formatMessage({ id: 'schemas.supplier.supplierUsage' })}>
              <CategoryChip
                label={formatMessage({
                  id: `schemas.provider.supplierUsageValues.${provider.supplierUsage ?? SupplierUsage.NotSelected}`,
                })}
                hasValue={hasSupplierUsage}
              />
            </CategoryRow>

            <CategoryRow label={formatMessage({ id: 'schemas.supplier.tier' })}>
              <CategoryChip
                label={
                  hasTier
                    ? formatMessage({ id: 'schemas.supplier.tierNumber' }, { number: provider.tier })
                    : formatMessage({ id: 'general.notSelected' })
                }
                hasValue={hasTier}
              />
            </CategoryRow>
          </>
        )}
        <CategoryRow label={formatMessage({ id: 'schemas.provider.priority' })}>
          <CategoryChip
            label={formatMessage({
              id: `schemas.provider.priorityValues.${provider.priority ?? ProviderPriority.NotSelected}`,
            })}
            hasValue={hasPriority}
          />
        </CategoryRow>
        <CategoryRow label={formatMessage({ id: 'schemas.organization.duns' })}>
          <CategoryChip
            label={provider.duns || formatMessage({ id: 'general.notAdded' })}
            hasValue={Boolean(provider.duns)}
          />
        </CategoryRow>

        <CategoryRow label={formatMessage({ id: 'schemas.organization.customId' })}>
          <CategoryChip
            label={provider.customId || formatMessage({ id: 'general.notAdded' })}
            hasValue={Boolean(provider.customId)}
          />
        </CategoryRow>
      </Stack>
    </Box>
  )
}

export default CategoriesSection
